"use client";

import { useCallback, useEffect, useMemo, useState } from "react";

import type { PhraseChord } from "@/components/learn-piece/phrase-chords";
import type { Hint } from "@/contexts/hint-context";
import { usePlaybackState, useStudio } from "@/contexts/studio-context";
import { chordProseParts } from "@/components/learn-piece/s0-copy";
import { useNoteNotation } from "@/hooks/use-notation";
import {
  JOURNEY_HINT_COLOR_LEFT_PRACTICE,
  JOURNEY_HINT_COLOR_RIGHT_PRACTICE,
} from "@/hooks/journey/journey-hint-mapping";
import { useHintPublisher } from "@/hooks/use-hint-publisher";
import { recordHarmonyPhase } from "@/hooks/use-harmony-analytics";
import { windowRhythm, type WindowRhythm } from "@/lib/harmony/rhythm";
import { type Frieze } from "@/lib/harmony/frieze";
import { chordPosition } from "@/lib/harmony/position";
import { useChordAudition } from "@/hooks/harmony/use-chord-audition";
import { useSegmentScroll } from "@/hooks/harmony/use-segment-scroll";
import { useRhythmTake } from "@/hooks/harmony/use-rhythm-take";
import { useHarmonyCursor } from "@/hooks/harmony/use-harmony-cursor";
import { useTransportSeek } from "@/hooks/harmony/use-transport-seek";
import { AUDITION_RATE, auditionSeconds } from "@/lib/harmony/audition";
import {
  chordIndexAtTick,
  unitAtTick,
  unitsChained,
  windowReach,
  type HarmonyWindow,
} from "@/lib/harmony/cursor";
import type { HarmonyContext } from "@/lib/harmony/hud";
import { isSeamRung } from "@/lib/harmony/scene";
import { chordNoteColumns, handFingers } from "@/lib/harmony/chord-notes";
import { spellingOf } from "@/components/learn-piece/phrase-chords";
import { chordPitchClasses, windowToneName } from "@/lib/music/theory-helpers";
import type { Phrase, PhraseGrip } from "@/lib/learning-journey/types";
import type { Observation } from "@/lib/levels/client";
import { describe as describeObservation } from "@/lib/levels/words";
import { ChordNotes } from "./chord-notes";
import { SeamScreen } from "./seam-screen";

/**
 * L'écran de jeu du parcours harmonique : le régime **libre** de l'étape qui
 * enchaîne.
 *
 * De haut en bas : le bandeau, puis les noms de l'accord en grand avec leurs
 * doigtés et le rail de rythme. C'est tout l'écran, et c'est le même aux neuf
 * niveaux.
 *
 * ## Un seul décor, et pourquoi la scène prend toute la hauteur
 *
 * La vue de studio lui donne toute la hauteur, clavier du roll compris, et la
 * scène la prend : son fond est opaque, elle couvre le clavier, et la réglette
 * d'octaves prend en bas la place de l'instrument. On ne joue pas des notes
 * ici, on **prend un accord** ; ce que le clavier disait — *où* sont les mains
 * — se dit en une bande, et la place gagnée revient à ce qu'on lit vraiment.
 *
 * Cet écran a eu un second décor, où un couloir de chute s'ouvrait à partir du
 * niveau 5 et où les notes tombaient jusqu'aux touches. Les niveaux 5 à 9 ne
 * sont pas opérationnels, personne n'y arrivait, et une seconde mise en page
 * qu'aucun écran n'atteint est une deuxième réponse à « à quoi ressemble le
 * parcours » que personne ne peut vérifier. Voir `scene.ts`, qui garde le
 * raisonnement et l'endroit où le reprendre.
 *
 * ## L'écran dit un fait par objet
 *
 * La phrase du bandeau dit ce que le niveau ajoute sur l'accord courant ; les
 * noms des notes, en grand et doigtés, disent l'accord lui-même ; ce qui vient
 * se lit sur le rail, sous les noms. Chaque objet porte un fait, et aucun ne
 * redit ce qu'un autre porte déjà : le chiffre romain est écrit avec les
 * noms.
 *
 * Le bandeau a porté un fil d'Ariane — les accords de l'unité en cours, dans
 * l'ordre, avec le courant marqué. Il est parti avec l'unité : la suite des
 * accords est déjà gravée sur le rail, à sa distance, et un fil qui la redit
 * en haut à gauche était une seconde réponse à « lequel maintenant ».
 *
 * ## Un seul fil, du premier accord du niveau au dernier
 *
 * Cet écran a travaillé unité par unité : le plan de phrases du morceau, le
 * même que celui du parcours par cœur, découpait le niveau en unités, la
 * scène était remontée à chacune, et son enchaînement repartait de la
 * première prise. Le découpage ne servait à rien de visible — pas de
 * répétition par unité, pas de pas entre deux — et il coûtait ce qu'un
 * remontage coûte : à chaque frontière d'unité, le son s'arrêtait net et la
 * tête repartait de zéro, exactement là où la grâce venait de rendre la
 * musique continue partout ailleurs.
 *
 * Le niveau est donc une seule suite de fenêtres, et cet écran la traverse
 * d'un bout à l'autre sans être remonté. Les unités existent encore, comme
 * grain de mesure : c'est sur elles que la session compte ce qui a été
 * atteint et enchaîné, et c'est `onUnitChained` qui le lui dit. Le joueur, lui,
 * ne les voit pas.
 *
 * ## Il ne connaît pas la machine à états, mais il en est la porte
 *
 * Cet écran **substitue** l'étape libre au lieu de se superposer à elle : deux
 * bandeaux empilés seraient deux réponses à « où en suis-je ». Substituer a un
 * prix, et il a été payé une fois en dette : l'écran remplacé était celui qui
 * disait au parcours qu'un passage venait d'être fait, et rien ne le disait
 * plus. Un niveau ne se traversait pas, il s'arrêtait sur son dernier accord.
 *
 * Il le dit donc maintenant, par `onChained`, et il ne dit que ça : faire
 * sonner le dernier accord du niveau jusqu'au bout **est** un passage,
 * exactement comme la fin d'un run l'est dans le parcours par cœur. Ce que le
 * parcours en fait ensuite appartient à `harmony-level`, qui tient le niveau.
 *
 * Ce qui suit, en revanche, ne lui appartient pas : l'unité **en rythme** et la
 * traversée gardent leurs porteurs, `rhythm-state` et `chain-state`. Ce sont
 * eux qui possèdent le transport, le décompte, le tempo et la lecture du
 * passage, et cet écran n'a rien de tout cela. Lui prendre ces deux pas
 * revenait à supprimer le métronome du parcours harmonique.
 *
 */

/**
 * Qui allume les touches, du point de vue du registre des indices.
 *
 * Nommé et distinct de `journey-grip` : deux écrans qui publieraient sous le
 * même nom se retireraient mutuellement leurs touches en se remplaçant.
 */
const HINT_SOURCE = "harmony-chain";

/**
 * Les niveaux dont la couture a déjà été montrée, pour cette session.
 *
 * Hors React et hors stockage : c'est un **moment**, pas un état du joueur. Le
 * ranger sur le disque en ferait une chose à effacer à la fermeture d'un compte
 * et à migrer au prochain schéma, pour éviter de remontrer une carte de dix
 * secondes à quelqu'un qui revient trois semaines plus tard, et qui a
 * probablement intérêt à la revoir.
 */
const crossedSeams = new Set<string>();

function useSeam(rungId: string): { readonly crossed: boolean; readonly cross: () => void } {
  const [, bump] = useState(0);

  return {
    crossed: crossedSeams.has(rungId),
    cross: () => {
      crossedSeams.add(rungId);
      bump((n) => n + 1);
    },
  };
}

/** Le rythme d'une fenêtre absente : rien à jouer, rien à graduer. */
const EMPTY_RHYTHM: WindowRhythm = { gates: [], left: [], right: [] };

/**
 * La couture prend l'écran, sur le fond de la scène.
 *
 * Tout l'écran, clavier du roll compris, depuis que la vue de niveau descend
 * jusqu'en bas. C'est une carte qu'on lit avant de jouer : rien ne tombe
 * derrière elle, et il n'y a donc pas de chute à ne pas couper.
 */
function Overlay({ children }: { readonly children: React.ReactNode }) {
  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        zIndex: 30,
        background: "var(--color-bg-deep)",
        pointerEvents: "auto",
      }}
    >
      {children}
    </div>
  );
}

/**
 * Une fenêtre de curseur, à partir d'un accord du niveau.
 *
 * Les bornes sont celles de l'accord rogné à l'étendue du morceau, c'est-à-dire
 * celles de l'analyse partout sauf aux deux bouts. Elles ont été rognées à
 * l'unité qui portait l'accord, un reste du temps où le niveau se jouait unité
 * par unité. Voir `playableChords`.
 */
function windowOf(entry: PhraseChord): HarmonyWindow {
  return {
    id: entry.chord.id,
    symbol: entry.chord.symbol,
    romanNumeral: entry.chord.romanNumeral,
    root: entry.chord.root,
    quality: entry.chord.quality,
    tick: entry.startTick,
    endTick: entry.endTick,
  };
}

export function HarmonyScene({
  harmony,
  entries,
  grips,
  units,
  frieze,
  bpm,
  ppq,
  beatTicks,
  beatsPerBar,
  tickToSeconds,
  onChained,
  onUnitChained,
}: {
  readonly harmony: HarmonyContext;
  /**
   * Ce que ce niveau donne à jouer, fenêtre d'accord par fenêtre, dans
   * l'ordre, du premier accord du niveau au dernier.
   *
   * Les fenêtres que le niveau laisse vides n'y sont pas : voir
   * `playableChords`, que l'appelant a déjà appliqué.
   */
  readonly entries: readonly PhraseChord[];
  /** Les prises du niveau, main par main, d'où se lit le doigté. */
  readonly grips: {
    readonly left: readonly PhraseGrip[];
    readonly right: readonly PhraseGrip[];
  };
  /**
   * Les unités du niveau, pour la mesure et pour elle seule.
   *
   * L'écran ne les montre pas ; il dit à la session laquelle est sous les
   * mains et laquelle vient d'être enchaînée. Voir la note d'en-tête.
   */
  readonly units: readonly Phrase[];
  /**
   * Tout ce que le niveau donne à jouer, bout à bout : le ruban du rail.
   *
   * Elle vient du niveau et non de l'unité, et c'est le point : une unité tient
   * souvent un seul accord, donc une frise d'unité serait la fenêtre d'avant
   * sous un autre nom. Voir `harmony-level`, qui la mémorise une fois pour le
   * niveau.
   */
  readonly frieze: Frieze;
  /** Les accords du morceau. */
  readonly bpm: number;
  readonly ppq: number;
  /** La durée d'un temps du morceau, en ticks. */
  readonly beatTicks: number;
  readonly beatsPerBar: number;
  /** Les ticks en secondes, pour faire entendre le passage d'origine. */
  readonly tickToSeconds: (tick: number) => number;
  /**
   * Le niveau vient d'être enchaîné : son dernier accord a fini de sonner.
   *
   * Un passage est un événement, pas un état : l'appelant ne le reçoit qu'une
   * fois par traversée.
   */
  readonly onChained: () => void;
  /**
   * Une unité vient d'être enchaînée : la fenêtre qui la termine a fini de
   * sonner.
   *
   * Pour la session, qui compte en unités ce que le joueur a atteint. Rien à
   * l'écran n'en dépend.
   */
  readonly onUnitChained: (unitId: string) => void;
}) {
  /**
   * Le régime se lit sur le transport, il ne se déclare pas.
   *
   * En rythme, c'est la lecture qui dit où l'on en est ; à l'arrêt, c'est la
   * main. Un troisième état n'existerait pas : un écran immobile en régime
   * chronométré serait un curseur qui n'avance pas, c'est-à-dire le régime
   * libre sous un autre nom.
   */
  const playing = usePlaybackState();
  const regime = playing ? "timed" : "free";
  const { studio } = useStudio();
  const { crossed, cross } = useSeam(harmony.rungId);

  /** Les fenêtres, telles que le curseur les lit. Voir `windowOf`. */
  const windows = useMemo<HarmonyWindow[]>(() => entries.map(windowOf), [entries]);

  /**
   * Où le transport en est, dans ce niveau.
   *
   * C'est **lui** qui dit quel accord est sous les mains, et non un compteur
   * interne : cet écran n'est pas une liste d'accords posée
   * devant le roll, c'est le roll dit autrement. Cliquer une mesure dans la
   * minimap ou une section dans la structure déplace donc l'accord montré, et
   * prendre l'accord déplace le roll. Une seule tête de lecture pour les deux.
   */
  const [tick, setTick] = useState(0);

  useTransportSeek(setTick);

  /*
    Pendant la lecture, personne ne prend d'accord et la tête de lecture ne se
    déplace pas à la main : c'est le régime en rythme qui tient l'écran. On
    n'ancre donc pas, sans quoi une prise fortuite ferait sauter le transport
    sous les oreilles de quelqu'un qui écoute.
  */
  const anchored = playing ? -1 : Math.max(chordIndexAtTick(windows, tick), 0);

  /**
   * L'accord confirmé fait avancer le transport, et rien d'autre.
   *
   * Sur le dernier accord du niveau il n'y a plus de fenêtre où aller : c'est
   * le niveau qui est fini, et `onChained` le dit à qui le tient.
   *
   * ## Il vise la fenêtre d'après **celle qu'il vient de faire**
   *
   * Et non celle d'après là où l'on est. Les deux se valaient tant que ce
   * rappel était le seul à déplacer la tête ; depuis que le niveau la fait
   * glisser d'une porte à la suivante, le défilement se conclut sur la frappe
   * qui ouvre la fenêtre d'après — donc l'accord ancré a déjà avancé d'un cran
   * quand la minuterie parle. Lire `anchored` ici sautait alors une fenêtre sur
   * deux.
   *
   * Le rang se retrouve par l'accord, ce qui rend le geste indifférent à
   * l'instant où il arrive et au chemin par lequel la tête est arrivée là. Si
   * elle y est déjà, le `seekToTick` ne fait rien de plus, et c'est ce qui
   * permet aux deux chemins de coexister sans se compter deux fois.
   */
  const settle = useCallback(
    (chordId: string) => {
      const at = entries.findIndex((entry) => entry.chord.id === chordId);

      if (at < 0) {
        return;
      }

      const next = windows[at + 1];

      // Les unités que cette fenêtre finit de traverser : la session les
      // compte, l'écran n'en fait rien. Plus d'une quand un accord tenu en
      // couvre une entière.
      for (const unitId of unitsChained(units, entries[at].startTick, next?.tick ?? null)) {
        onUnitChained(unitId);
      }

      if (next === undefined) {
        onChained();

        return;
      }

      // La minuterie parle avant que le défilement du segment ait atterri : il
      // part à la frappe, le vol au premier son. Poser la tête maintenant ne
      // fait donc pas sauter l'image, le décalage en cours l'absorbe.
      studio?.seekWithoutJump(next.tick);
    },
    [studio, entries, windows, units, onChained, onUnitChained],
  );

  const anchoredWindow = anchored < 0 ? null : (entries[anchored] ?? null);
  const anchoredTick = anchoredWindow?.startTick ?? null;

  /**
   * La couture passe devant le niveau, ou le niveau est à l'écran — et sous
   * quelle unité, pour la session.
   *
   * Lu ici et pas au niveau au-dessus : `crossedSeams` vit dans ce fichier, et
   * le faire remonter donnerait au niveau une seconde réponse à « qu'est-ce
   * qu'on montre » — celle qui se tromperait le jour où la règle changerait.
   *
   * L'unité est celle de la fenêtre ancrée, ou aucune pendant la lecture, où
   * rien n'est sous les mains.
   */
  const unitId = unitAtTick(units, anchoredTick);

  useEffect(() => {
    recordHarmonyPhase(
      harmony.rungId,
      !crossed && isSeamRung(harmony.rungIndex) ? "seam" : "unit",
      unitId,
    );
  }, [crossed, harmony.rungId, harmony.rungIndex, unitId]);


  /**
   * Combien de temps le morceau sonne sous l'accord courant, ralenti compris.
   *
   * C'est la durée de la fenêtre d'écoute, donc la même conversion que celle
   * qui fabrique les notes de l'extrait : l'accord ne passe pas avant sa fin,
   * et le compte à rebours sous les notes montre ce qu'il en reste.
   */
  /**
   * L'écran est prêt à valider une frappe.
   *
   * Toujours vrai depuis que le compte d'ouverture a disparu. Il tenait la
   * porte fermée pendant les quatre battements, et ces quatre battements
   * n'annonçaient plus rien : c'est le joueur qui déclenche la musique, porte
   * après porte, donc il n'y a plus de départ à attendre ensemble. Le champ
   * reste nommé parce que la règle, elle, n'a pas changé : une frappe ne compte
   * que quand l'écran l'attend.
   */
  const armed = true;

  const holdMs = useMemo(() => {
    if (anchoredWindow === null) {
      return 0;
    }

    const start = auditionSeconds(tickToSeconds, anchoredWindow.startTick);

    return Math.max(auditionSeconds(tickToSeconds, anchoredWindow.endTick) - start, 0) * 1000;
  }, [anchoredWindow, tickToSeconds]);

  /**
   * Ce qu'il y a à jouer dans la fenêtre, main par main.
   *
   * Sur la fenêtre ancrée et non sur celle du curseur : c'est la même que celle
   * qui donne `holdMs`, donc le rail et la lueur mesurent le même objet.
   *
   * Lu sur les notes **du niveau** et non sur celles du morceau : c'est ce
   * qu'on demande aux mains, et c'est ce que le rail doit montrer. Le morceau,
   * lui, se fait entendre par-dessus.
   */
  const rhythm = useMemo(
    () =>
      anchoredWindow === null
        ? EMPTY_RHYTHM
        : windowRhythm({
            startTick: anchoredWindow.startTick,
            endTick: anchoredWindow.endTick,
            left: anchoredWindow.left.notes,
            right: anchoredWindow.right.notes,
            beatTicks,
          }),
    [anchoredWindow, beatTicks],
  );

  /**
   * Le chiffrage, tel que le rail l'affiche.
   *
   * Reconstitué depuis ce que le niveau reçoit — le numérateur et la durée d'un
   * temps — plutôt que demandé en plus : les deux viennent déjà de la même
   * signature, et un troisième champ pourrait en diverger.
   */
  const meter = beatTicks > 0 ? `${beatsPerBar}/${Math.round((ppq * 4) / beatTicks)}` : null;

  /**
   * Le tempo qu'on **entend**, ralenti compris.
   *
   * C'est sur lui qu'on compte les temps du rail. Afficher le tempo écrit
   * ferait compter à côté de ce qui sonne.
   */
  const heardBpm = bpm > 0 ? bpm * AUDITION_RATE : null;

  /** Un tick du morceau, en millisecondes d'écoute, ralenti compris. */
  const msAt = useCallback(
    (tick: number) => auditionSeconds(tickToSeconds, tick) * 1000,
    [tickToSeconds],
  );

  const take = useRhythmTake({
    gates: rhythm.gates,
    startTick: anchoredWindow?.startTick ?? 0,
    // Jusqu'à la frappe suivante, et non jusqu'au bord de cette fenêtre : les
    // deux ne coïncident pas dès que l'analyse laisse un silence entre deux
    // accords, et la tête de lecture s'y posait sans plus rien pouvoir ouvrir.
    // Voir `windowReach`.
    untilTick: windowReach(windows, anchored),
    chordId: anchoredWindow?.chord.id ?? "",
    armed,
    msAt,
    onSettled: settle,
  });

  /**
   * Le passage d'origine, lancé par la frappe et découpé par elle.
   *
   * Seulement à l'arrêt, pour la même raison qu'on n'ancre pas pendant la
   * lecture : le régime en rythme fait déjà entendre le morceau, et un extrait
   * par-dessus serait un second son sur la même mesure.
   *
   * Ce n'est plus la fenêtre entière mais **le segment de la porte franchie**,
   * d'une frappe à la suivante. Le mécanisme est le même qu'avant, appliqué un
   * cran plus fin : rien ne sonne tant qu'on n'a pas joué, et ce qui sonne
   * s'arrête là où il faut rejouer. Le joueur mène la musique au lieu de courir
   * après elle, à chaque frappe et plus seulement à chaque accord.
   *
   * Une fenêtre à une seule porte — les niveaux un et deux — retrouve
   * exactement le comportement d'avant : un segment, du début à la fin.
   */
  /**
   * Le segment qui sonne à cet instant, dit par le lecteur lui-même.
   *
   * C'est là-dessus que la tête de lecture du rail se cale : demander un
   * passage et l'entendre ne sont pas le même instant tant que les échantillons
   * du programme chargent, et une tête partie à la demande prendrait cette
   * avance sans jamais la rendre.
   */
  const [sounding, setSounding] = useState<string | null>(null);

  useChordAudition({
    notes: harmony.pieceNotes,
    tickToSeconds,
    window: anchoredWindow === null || !armed ? null : take.segment,
    onStarted: setSounding,
  });

  /**
   * Le niveau déplace la tête de lecture du studio, porte après porte.
   *
   * C'est le pendant exact de l'écoute juste au-dessus, et les deux reçoivent
   * le même segment : l'une le fait entendre, l'autre l'emmène. Le roll, la
   * minimap et la partition avancent alors ensemble, parce que le studio
   * avance, et non parce qu'ils se seraient synchronisés entre eux.
   *
   * Un défilement du studio et non une lecture du transport : voir
   * `useSegmentScroll` pour ce que la seconde aurait entraîné.
   */
  useSegmentScroll({
    // Le segment qui **sonne**, et non celui qu'on vient de demander : les
    // échantillons du programme peuvent encore charger au premier accord, et
    // une tête de lecture partie à la frappe prendrait l'avance du chargement
    // sans jamais la rendre. C'est le lecteur qui dit quand le passage part.
    segment: take.segment !== null && sounding === take.segment.id ? take.segment : null,
    ms: take.runningMs,
    // La grâce et le retour, eux, ne dépendent pas du lecteur : la porte
    // s'ouvre à la date du segment, que le son ait pris du retard ou non.
    grace: take.grace,
    haltTick: take.haltTick,
  });

  // Le rang du régime libre vient du transport, et non d'un compteur : voir
  // `anchored`.
  const cursor = useHarmonyCursor({ chords: windows, regime, index: anchored });

  const entry: PhraseChord | null = cursor.index >= 0 ? (entries[cursor.index] ?? null) : null;

  /**
   * L'accord courant, note par note, avec le doigt de chaque main.
   *
   * Les touches sont celles que l'accord demande, tenues comprises : c'est
   * exactement l'ensemble sur lequel l'enchaînement décide qu'il est pris, donc
   * « tous les noms allumés » et « l'accord est pris » ne peuvent pas diverger.
   *
   * Le doigté vient des prises **du niveau**, publiées par l'analyseur avec
   * ses notes. Il n'est pas calculé ici : ce serait une seconde réponse, plus
   * faible, à une question que son solveur a déjà tranchée.
   */
  const columns = useMemo(() => {
    if (entry === null) {
      return [];
    }

    return chordNoteColumns({
      left: [...entry.left.midis, ...entry.left.sustainedMidis],
      right: [...entry.right.midis, ...entry.right.sustainedMidis],
      rootSpelling: entry.chord.root,
      rootPitchClass: entry.chord.rootPitchClass,
      chordPitchClasses: chordPitchClasses(entry.chord.rootPitchClass, entry.chord.quality),
      // La frise porte déjà la tonique du morceau, et c'est la même que celle
      // qui a orthographié la portée : deux réponses se verraient.
      keyTonic: frieze.keyTonic,
      spelling: spellingOf(entry),
      fingers: {
        left: handFingers(grips.left, entry.startTick, entry.endTick),
        right: handFingers(grips.right, entry.startTick, entry.endTick),
      },
    });
  }, [entry, grips, frieze.keyTonic]);

  /**
   * L'accord courant, allumé sur le vrai piano.
   *
   * La miniature dit **quoi** — la forme sous la main, et le nom des notes. Le
   * piano du roll dit **où**, sur les quatre-vingt-huit touches, ce qui est la
   * seule question que la miniature ne peut pas répondre : elle montre trois
   * octaves, l'instrument en a sept, et un joueur qui a compris la prise peut
   * encore la poser une octave à côté.
   *
   * Les deux couleurs sont celles du parcours en pratique et pas en
   * découverte : cet écran enchaîne, il ne fait pas chercher. Et ce sont les
   * mêmes teintes de main que partout ailleurs, donc rien de nouveau à
   * apprendre en arrivant ici.
   */
  const hint = useMemo<Hint | null>(() => {
    if (entry === null) {
      return null;
    }

    return {
      source: HINT_SOURCE,
      midis: [
        ...entry.left.midis.map((midi) => ({ midi, color: JOURNEY_HINT_COLOR_LEFT_PRACTICE })),
        ...entry.right.midis.map((midi) => ({ midi, color: JOURNEY_HINT_COLOR_RIGHT_PRACTICE })),
      ],
    };
  }, [entry]);

  useHintPublisher(hint);

  /**
   * La même sélection sur le roll, qui la lit par sa propre voie.
   *
   * Deux appels et pas un, parce que ce sont deux surfaces : le publieur
   * colore les touches, celui-ci désigne les notes de la fenêtre. `grip-state`
   * fait déjà exactement cette paire, et s'en écarter ici ferait deux
   * conventions pour un seul geste.
   */
  useEffect(() => {
    if (!studio) {
      return;
    }

    if (entry === null) {
      studio.clearHintSelection();

      return;
    }

    studio.setHintSelection([...entry.midis], entry.startTick, entry.endTick);
  }, [studio, entry]);

  /**
   * L'accord en toutes lettres, dans la notation des **notes**.
   *
   * `Ré mineur` au-dessus de `Ré · Fa · La`, et non `Dm` : le nom et les notes
   * qu'il désigne sont à dix pixels l'un de l'autre, et les voir se contredire
   * fait douter de la seule chose que cet écran affirme. Le symbole reste au
   * rail, qui est un chiffrage et suit à ce titre le réglage des symboles
   * d'accord.
   */
  const notation = useNoteNotation();

  /**
   * La position de l'accord sous les mains.
   *
   * Un rang et non des mots : c'est la pastille qui le nomme, et elle a besoin
   * du chiffre pour choisir sa couleur. Elle est là sur tous les accords,
   * position fondamentale comprise, donc la géométrie ne bouge pas.
   *
   * Celle de l'accord **suivant** n'existe plus : elle n'a jamais servi qu'à
   * l'annonce sous les notes, et ce qui vient est maintenant gravé sur la
   * partition, à sa distance, avec sa position à côté de son nom.
   */
  const inversion = useMemo(
    () =>
      entry === null
        ? 0
        : chordPosition(entry.chord.rootPitchClass, [
            ...entry.right.midis,
            ...entry.right.sustainedMidis,
          ]),
    [entry],
  );

  /**
   * Le même nom, en deux morceaux, pour le milieu de l'écran.
   *
   * Là-bas la note est teintée et la qualité reste blanche, et la coupe ne
   * peut pas se faire sur la chaîne finie : « Si bémol majeur » a trois mots
   * dont deux forment la note. Le bandeau, lui, écrit la phrase d'une traite
   * et se contente de la chaîne.
   */
  const currentChord = cursor.current;

  const currentParts =
    currentChord === null
      ? { root: "", quality: null }
      : chordProseParts({ chord: currentChord }, notation);

  /**
   * La note sur laquelle l'accord se pose, quand ce n'est pas sa fondamentale.
   *
   * C'est la barre du symbole, le « /C♯ » de « F♯m/C♯ » : la plus basse note
   * qui sonne dans la fenêtre, orthographiée comme les lettres de l'accord.
   * `null` à l'état fondamental, où « F♯m/F♯ » ne dirait rien.
   */
  const slashBass = useMemo(() => {
    if (entry === null || entry.bassMidi === null) {
      return null;
    }

    const pitchClass = ((entry.bassMidi % 12) + 12) % 12;

    if (pitchClass === entry.chord.rootPitchClass) {
      return null;
    }

    return windowToneName({
      spelled: spellingOf(entry)?.get(entry.bassMidi) ?? null,
      pitchClass,
      rootSpelling: entry.chord.root,
      rootPitchClass: entry.chord.rootPitchClass,
      chordPitchClasses: chordPitchClasses(entry.chord.rootPitchClass, entry.chord.quality),
      keyTonic: frieze.keyTonic,
    });
  }, [entry, frieze.keyTonic]);

  /**
   * Le chiffre romain de l'accord courant, ou rien.
   *
   * Même règle que dans le bandeau, et pas une règle voisine : un degré ne
   * s'affiche que si l'analyseur répond des degrés de ce morceau. Le milieu de
   * l'écran ne peut pas en montrer un que le bandeau tait.
   */
  const currentNumeral =
    harmony.degreesTrusted && cursor.current !== null ? cursor.current.romanNumeral : "";

  /**
   * La couture, montrée une fois avant de jouer.
   *
   * Elle tombe à une limite de niveau, donc sur un écran immobile : les mains
   * ne changent pas de place pendant un niveau.
   */
  if (!crossed && isSeamRung(harmony.rungIndex)) {
    return (
      <Overlay>
        <SeamScreen level={harmony} onStart={cross} />
      </Overlay>
    );
  }

  return (
    <div
      className="flex flex-col"
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        // La scène prend toute la hauteur, clavier du roll compris : sa
        // réglette d'octaves tient la place de l'instrument.
        bottom: 0,
        zIndex: 30,
        // Opaque, donc les notes qui tombent disparaissent dessous. C'est la
        // thèse du parcours, écrite en une propriété.
        background: "var(--color-bg-deep)",
        pointerEvents: "auto",
      }}
    >
      {/*
        L'accord prend tout l'écran. Il n'y a plus de bandeau au-dessus : le nom
        du niveau est parti, et la phrase qui dit ce que le niveau ajoute s'est
        posée entre l'accord et la partition.
      */}
      <div
        className="flex items-center justify-center"
        style={{
          flex: "1 1 auto",
          // La marge passe **dans** `ChordNotes`, sur son contenu. La lueur y
          // est posée sur toute la boîte, et une marge ici la tiendrait à
          // distance des bords : elle s'arrêterait net à dix-huit pixels du
          // vide, ce qui se lit comme un cadre noir autour d'une lumière
          // plutôt que comme une lumière.
          padding: 0,
          minHeight: 0,
        }}
      >
        <ChordNotes
          columns={columns}
          chordId={entry?.chord.id ?? ""}
          name={currentParts}
          bass={slashBass}
          inversion={inversion}
          numeral={currentNumeral}
          holdMs={holdMs}
          frieze={frieze}
          beatTicks={beatTicks}
          struck={take.struck}
          meter={meter}
          bpm={heardBpm}
          explanation={explain(cursor.current, harmony.news)}
        />
      </div>
    </div>
  );
}

/**
 * Ce que le niveau ajoute sur l'accord sous les mains, en une phrase.
 *
 * `null` quand ce niveau ne change rien ici. Ce n'est pas un cas d'erreur : un
 * niveau n'a pas quelque chose à ajouter à chaque accord.
 */
function explain(current: HarmonyWindow | null, news: readonly Observation[]): string | null {
  if (current === null) {
    return null;
  }

  const here = news.find((observation) => observation.chordId === current.id);

  return here === undefined ? null : sentence(describeObservation(here));
}

/**
 * Une phrase, à partir d'une incise.
 *
 * `describe()` écrit des fragments en minuscule : la phrase prend sa majuscule
 * et son point. Une nature d'observation que `words.ts` ne connaîtrait pas
 * tombe hors du `switch` et se tait au lieu de casser l'écran.
 */
function sentence(fragment: string | undefined): string | null {
  if (fragment === undefined || fragment === "") {
    return null;
  }

  return `${fragment.charAt(0).toUpperCase()}${fragment.slice(1)}.`;
}
